import React, { ReactElement, useContext, useEffect, useRef, useState } from 'react'
import Highcharts from 'highcharts2/highstock'
import styled, { useTheme } from 'styled-components'
import i18n from 'live/services/i18n'
import {
    renderButtons,
    StaticDataType,
    ButtonOptions,
    STATIC_DATA_STATES,
    renderCreationTooltip,
    DeleteConfirmationModal
} from '../chart-components'
import { ChartContainer, ChartTitle } from '../chart-components-style'
import { getPhaseChartOptions } from './chart-options'
import { StaticDataModal } from '../modal-components'
import { PhaseModal } from './PhaseModal'
import { deleteSingleEntity, SECTION_API } from '../../api/api'
import { StaticDataContext } from '../../context/StaticDataContext'
import { RawPhaseEvent } from '../../utils/type'

require('highcharts/modules/xrange')(Highcharts)

export enum ModalPhaseStatus {
    FIRST_PHASE = 'empty dataset',
    NEW_SECTION = 'new section',
    END_SECTION = 'end section',
    UPDATE_SECTION = 'update section'
}

export const PhaseChart = (): ReactElement => {
    // Static Data Context
    const {
        buttonsRef,
        buttonController,
        setButtonController,
        currentChart,
        setAddEventButtonVisible,
        chartType,
        tooltipPosition,
        setTooltiPosition,
        widgetData,
        widgetTimeSpan,
        refresh,
        tryOpenModal,
        reloadData,
        checkBlockedTrackBeforeAction
    } = useContext(StaticDataContext)

    // Chart States
    const [highchartsElement, setHighchartsElement] = useState<Highcharts.Chart | null>(null)
    const [chartLoaded, setChartLoaded] = useState<boolean>(false)
    const [chartHeight, setChartHeight] = useState<number>(0)
    const chartRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()

    // Modal States
    const [currentModalEvent, setCurrentModalEvent] = useState<RawPhaseEvent>(null)
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const closeModal = (): void => setIsModalOpen(false)
    const openModal = (rawEvent: RawPhaseEvent): void => {
        setCurrentModalEvent(rawEvent)
        setSectionTooltipMessage(null)
        setIsModalOpen(true)
    }

    // Delete Confirmation Modal State
    const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] = useState<boolean>(false)
    const closeDeleteConfirmationModal = (): void => setIsDeleteConfirmationModalOpen(false)
    const openDeleteConfirmationModal = (rawEvent: RawPhaseEvent): void => {
        setCurrentModalEvent(rawEvent)
        setIsDeleteConfirmationModalOpen(true)
    }

    const deleteSectionPhase = (sectionId: any) => {
        return deleteSingleEntity(SECTION_API, sectionId).then(() => {
            closeDeleteConfirmationModal()
            reloadData()
        })
    }

    function draw(): void {
        try {
            highchartsElement?.destroy()
            setChartLoaded(false)
            setHighchartsElement(
                Highcharts.chart(
                    chartRef?.current,
                    getPhaseChartOptions(
                        { timeSpan: widgetTimeSpan, height: chartHeight },
                        widgetData.phaseSeries,
                        theme,
                        openModal,
                        tryOpenModal,
                        setAddEventButtonVisible,
                        setTooltiPosition,
                        setSectionTooltipMessage,
                        checkBlockedTrackBeforeAction
                    ),
                    () => setChartLoaded(true)
                )
            )
        } catch (error) {
            // highchartsElement does not exits.
        }
    }

    useEffect(() => setChartHeight(chartRef.current.getBoundingClientRect().height), [])

    useEffect(() => draw(), [widgetData.phaseSeries])

    useEffect(() => {
        if (chartLoaded) {
            setTimeout(() => {
                draw()
            }, 300)
        }
    }, [refresh, chartHeight, theme])

    const [sectionTooltipMessage, setSectionTooltipMessage] = useState(null)

    const buttonProps = {
        buttonFunctions: [
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openDeleteConfirmationModal(currentChart?.rawEvent as RawPhaseEvent)
                    setButtonController(false)
                }),
            () =>
                checkBlockedTrackBeforeAction(currentChart, () => {
                    openModal(currentChart?.rawEvent as RawPhaseEvent)
                    setButtonController(false)
                })
        ],
        customIcons: [ButtonOptions.Delete, ButtonOptions.Confirm]
    }

    return (
        <>
            <ChartContainer>
                <StaticDataModal
                    isOpen={isModalOpen}
                    isDouble={false}
                    closeModal={closeModal}
                    content={<PhaseModal closeModal={closeModal} event={currentModalEvent} />}
                />
                <ChartTitle>
                    <span>{i18n(StaticDataType.PHASE).toUpperCase()}</span>
                </ChartTitle>
                <Chart ref={chartRef} />
                {buttonController && buttonController === STATIC_DATA_STATES.EMPTY_DISABLED ? (
                    <div></div>
                ) : buttonController && buttonController !== STATIC_DATA_STATES.EMPTY ? (
                    chartType === 'phase' && renderButtons(buttonsRef, currentChart, buttonProps)
                ) : (
                    chartType === 'phase' &&
                    renderCreationTooltip(
                        buttonsRef,
                        currentChart,
                        tooltipPosition,
                        buttonController,
                        sectionTooltipMessage
                    )
                )}
            </ChartContainer>
            <DeleteConfirmationModal
                isOpen={isDeleteConfirmationModalOpen}
                closeModal={closeDeleteConfirmationModal}
                confirmDeletion={() => {
                    deleteSectionPhase((currentChart?.rawEvent as RawPhaseEvent).id).then(() => {
                        currentChart.remove()
                        setButtonController(false)
                    })
                }}
            />
        </>
    )
}

const Chart = styled.div`
    height: 100%;
    width: 100%;
    max-height: 30px;
    min-height: 20px;
    margin-top: -16px;

    .highcharts-container {
        max-height: 30px;
        min-height: 20px;
        .highcharts-root {
            max-height: 30px;
            min-height: 20px;
            .highcharts-series-group {
                max-height: 30px;
                min-height: 20px;
                .highcharts-series highcharts-series-0 highcharts-xrange-series highcharts-tracker {
                    max-height: 30px;
                    min-height: 20px;
                    .highcharts-point {
                        max-height: 30px;
                        min-height: 20px;
                        .highcharts-point highcharts-partfill-original {
                            max-height: 30px;
                            min-height: 20px;
                        }
                    }
                }
            }
        }
    }
`
